import FacebookIcon from '@mui/icons-material/Facebook';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CustomDataGrid from 'components/common/CustomDataGrid';
import type React from 'react';
import { useMemo, useState } from 'react';
import { META_BLUE, NAVY, RADIUS_ITEM, STATUS_GOOD, STATUS_NEUTRAL, STATUS_WARN, WHATSAPP_GREEN } from 'styles/tokens';

import { formatCurrency, formatNumber } from '../drillDown/metaDrillDownPrimitives';
import type { CampaignRow, CampaignStatus, Channel } from '../types';
import { formatCampaignObjective } from '../utils/metaTypeLabels';

// Ratio (0–1) → "XX.X%". Distinct from `formatPct` in metaDrillDownPrimitives, which
// formats already-percentage values (e.g. CTR "1.23" → "1.23%") without scaling.
const formatRatioAsPct = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
};

const formatCtr = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}%`;
};

const crmRateColor = (rate: number | null): string => {
  if (rate === null) return STATUS_NEUTRAL;
  if (rate >= 0.5) return STATUS_GOOD;
  if (rate >= 0.3) return STATUS_WARN;
  return STATUS_NEUTRAL;
};

const channelBadgeColor: Record<Channel, string> = {
  whatsapp: WHATSAPP_GREEN,
  meta: META_BLUE,
};

const statusChipProps: Record<CampaignStatus, { label: string; bg: string; color: string }> = {
  active: { label: 'Activa', bg: alpha(STATUS_GOOD, 0.15), color: STATUS_GOOD },
  paused: { label: 'Pausada', bg: alpha(STATUS_WARN, 0.15), color: STATUS_WARN },
  unknown: { label: '—', bg: alpha(NAVY, 0.04), color: alpha(NAVY, 0.5) },
};

/** DataGrid needs `id: number`. We wrap each CampaignRow with a numeric grid id and
 *  keep the original semantic id in `rowKey` so row clicks can round-trip to the
 *  CampaignRow shape the drawer expects. */
interface CampaignGridRow extends Omit<CampaignRow, 'id'> {
  id: number;
  rowKey: string;
}

const toGridRow = (row: CampaignRow, index: number): CampaignGridRow => ({
  ...row,
  id: index,
  rowKey: row.id,
});

const ChannelBadge: React.FC<{ channel: Channel; accountName: string | null }> = ({ channel, accountName }) => {
  const color = channelBadgeColor[channel];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          bgcolor: alpha(color, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {channel === 'whatsapp' ? (
          <WhatsAppIcon sx={{ fontSize: 15, color }} />
        ) : (
          <FacebookIcon sx={{ fontSize: 15, color }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {accountName ? (
          <>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: NAVY,
                fontWeight: 600,
                fontSize: '0.82rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.15,
              }}
            >
              {accountName}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost, sans-serif',
                color: alpha(NAVY, 0.5),
                fontSize: '0.62rem',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {channel === 'whatsapp' ? 'WhatsApp' : 'Meta'}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 600, fontSize: '0.82rem' }}>
            {channel === 'whatsapp' ? 'WhatsApp' : 'Meta'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CampaignNameCell: React.FC<{ row: CampaignGridRow }> = ({ row }) => {
  const objective = formatCampaignObjective(row.objective);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          color: NAVY,
          fontWeight: 600,
          fontSize: '0.85rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
        }}
      >
        {row.campaignName}
      </Typography>
      {objective && (
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            color: alpha(NAVY, 0.5),
            fontWeight: 500,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {objective}
        </Typography>
      )}
    </Box>
  );
};

const buildColumns = (): GridColDef[] => [
  {
    field: 'channel',
    headerName: 'Canal',
    width: 200,
    renderCell: (params) => (
      <ChannelBadge channel={params.row.channel as Channel} accountName={params.row.accountName as string | null} />
    ),
  },
  {
    field: 'campaignName',
    headerName: 'Campaña',
    width: 240,
    renderCell: (params) => <CampaignNameCell row={params.row as CampaignGridRow} />,
  },
  {
    field: 'status',
    headerName: 'Estado',
    width: 110,
    renderCell: (params) => {
      const info = statusChipProps[params.row.status as CampaignStatus];
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={info.label}
            size="small"
            sx={{
              bgcolor: info.bg,
              color: info.color,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              borderRadius: RADIUS_ITEM,
            }}
          />
        </Box>
      );
    },
  },
  {
    field: 'spend',
    headerName: 'Gasto',
    width: 120,
    type: 'number',
    renderCell: (params) => formatCurrency(params.row.spend as number | null, params.row.currency as string | null),
  },
  {
    field: 'totalLeads',
    headerName: 'Leads',
    width: 90,
    type: 'number',
    valueFormatter: (value: number | null) => formatNumber(value),
  },
  {
    field: 'leadsContacted',
    headerName: 'Contactados',
    width: 120,
    type: 'number',
    valueFormatter: (value: number | null) => formatNumber(value),
  },
  {
    field: 'leadsActive',
    headerName: 'Activos',
    width: 100,
    type: 'number',
    valueFormatter: (value: number | null) => formatNumber(value),
  },
  {
    field: 'leadsInCrm',
    headerName: 'CRM',
    width: 90,
    type: 'number',
    valueFormatter: (value: number | null) => formatNumber(value),
  },
  {
    field: 'crmRate',
    headerName: 'Tasa CRM',
    width: 110,
    type: 'number',
    renderCell: (params) => {
      const rate = params.row.crmRate as number | null;
      return (
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            color: crmRateColor(rate),
            fontWeight: 700,
            fontSize: '0.82rem',
          }}
        >
          {formatRatioAsPct(rate)}
        </Typography>
      );
    },
  },
  {
    field: 'costPerContactedLead',
    headerName: 'CPL contactado',
    width: 140,
    type: 'number',
    renderCell: (params) =>
      formatCurrency(params.row.costPerContactedLead as number | null, params.row.currency as string | null),
  },
  {
    field: 'costPerCrmLead',
    headerName: 'CPL CRM',
    width: 120,
    type: 'number',
    renderCell: (params) =>
      formatCurrency(params.row.costPerCrmLead as number | null, params.row.currency as string | null),
  },
  {
    field: 'ctr',
    headerName: 'CTR',
    width: 90,
    type: 'number',
    valueFormatter: (value: number | null) => formatCtr(value),
  },
];

const compareValues = (a: unknown, b: unknown, direction: 'asc' | 'desc'): number => {
  if (a == null && b == null) return 0;
  if (a == null) return direction === 'asc' ? 1 : -1;
  if (b == null) return direction === 'asc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a;
  }
  const sa = String(a);
  const sb = String(b);
  const cmp = sa.localeCompare(sb, 'es', { numeric: true, sensitivity: 'base' });
  return direction === 'asc' ? cmp : -cmp;
};

interface UnifiedCampaignTableProps {
  rows: CampaignRow[];
  isLoading: boolean;
  onRowClick: (row: CampaignRow) => void;
}

const UnifiedCampaignTable: React.FC<UnifiedCampaignTableProps> = ({ rows, isLoading, onRowClick }) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'totalLeads', sort: 'desc' }]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const columns = useMemo(() => buildColumns(), []);

  const sortedRows = useMemo(() => {
    const primary = sortModel[0];
    if (!primary?.sort) return rows;
    return [...rows].sort((a, b) => {
      const field = primary.field as keyof CampaignRow;
      return compareValues(a[field], b[field], primary.sort as 'asc' | 'desc');
    });
  }, [rows, sortModel]);

  // Client-side pagination slice — mirrors the behavior of other CustomDataGrid callers
  // in this repo so the row count in the footer aligns with what's visible.
  const pagedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return sortedRows.slice(start, start + paginationModel.pageSize);
  }, [sortedRows, paginationModel]);

  const gridRows: CampaignGridRow[] = useMemo(() => pagedRows.map(toGridRow), [pagedRows]);

  const handleRowClick = (gridRow: CampaignGridRow) => {
    const original = rows.find((r) => r.id === gridRow.rowKey);
    if (original) onRowClick(original);
  };

  const noResultsMessage = isLoading ? 'Cargando campañas…' : 'No se encontraron campañas con los filtros actuales.';

  return (
    <CustomDataGrid<CampaignGridRow>
      rows={gridRows}
      columns={columns}
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      rowCount={sortedRows.length}
      onRowClick={handleRowClick}
      getRowClassName={() => ''}
      pageId="campaigns-control-panel"
      disableFilters
      noResultsMessage={noResultsMessage}
    />
  );
};

export default UnifiedCampaignTable;
